import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, Star, MessageSquare, Search, User, LogOut, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';

const ClientDashboard = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewModal, setReviewModal] = useState({ open: false, booking: null });
  const [reviewData, setReviewData] = useState({ rating: 5, comment: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const res = await api.get('/bookings');
      setBookings(res.data || []);
    } catch (err) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const cancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    
    try {
      await api.put(`/bookings/${bookingId}`, { status: 'cancelled' });
      toast.success('Booking cancelled');
      fetchBookings();
    } catch (err) {
      toast.error('Failed to cancel booking');
    }
  };

  const submitReview = async () => {
    if (!reviewData.comment.trim()) {
      toast.error('Please write a review');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/reviews', {
        booking_id: reviewModal.booking.id,
        lawyer_id: reviewModal.booking.lawyer_id,
        rating: reviewData.rating,
        comment: reviewData.comment
      });
      toast.success('Review submitted');
      setReviewModal({ open: false, booking: null });
      setReviewData({ rating: 5, comment: '' });
      fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-pending',
      accepted: 'badge-approved',
      declined: 'badge-rejected',
      completed: 'badge-completed',
      cancelled: 'badge-rejected'
    };
    return badges[status] || 'badge-pending';
  };

  const upcomingBookings = bookings.filter(b => ['pending', 'accepted'].includes(b.status));
  const pastBookings = bookings.filter(b => ['completed', 'cancelled', 'declined'].includes(b.status));

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="container-app py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Welcome, {user?.name}</h1>
            <p className="text-slate-500 mt-1">Manage your consultations</p>
          </div>
          <Link to="/lawyers">
            <Button className="bg-amber-600 hover:bg-amber-700 rounded-full" data-testid="find-lawyer-btn">
              <Search className="w-5 h-5 mr-2" />
              Find a Lawyer
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-6 rounded-2xl border border-slate-200"
          >
            <p className="text-3xl font-bold text-slate-900">{bookings.length}</p>
            <p className="text-slate-500 text-sm">Total Bookings</p>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white p-6 rounded-2xl border border-slate-200"
          >
            <p className="text-3xl font-bold text-slate-900">{upcomingBookings.length}</p>
            <p className="text-slate-500 text-sm">Upcoming</p>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white p-6 rounded-2xl border border-slate-200"
          >
            <p className="text-3xl font-bold text-slate-900">{pastBookings.filter(b => b.status === 'completed').length}</p>
            <p className="text-slate-500 text-sm">Completed</p>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white p-6 rounded-2xl border border-slate-200"
          >
            <p className="text-3xl font-bold text-amber-600">{pastBookings.filter(b => b.status === 'cancelled').length}</p>
            <p className="text-slate-500 text-sm">Cancelled</p>
          </motion.div>
        </div>

        {/* Bookings */}
        <div className="bg-white rounded-2xl border border-slate-200">
          <Tabs defaultValue="upcoming">
            <TabsList className="w-full border-b border-slate-200 rounded-t-2xl">
              <TabsTrigger value="upcoming" className="flex-1" data-testid="tab-upcoming">
                Upcoming ({upcomingBookings.length})
              </TabsTrigger>
              <TabsTrigger value="past" className="flex-1" data-testid="tab-past">
                Past ({pastBookings.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upcoming" className="p-6">
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="animate-pulse bg-slate-100 h-32 rounded-xl" />
                  ))}
                </div>
              ) : upcomingBookings.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No upcoming consultations</p>
                  <Link to="/lawyers">
                    <Button className="mt-4" variant="outline" data-testid="browse-lawyers-btn">
                      Browse Lawyers
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {upcomingBookings.map((booking, idx) => (
                    <motion.div
                      key={booking.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="p-4 bg-slate-50 rounded-xl border border-slate-200"
                      data-testid={`booking-${idx}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-slate-200 rounded-xl flex items-center justify-center font-bold text-slate-600">
                            {booking.lawyer?.user?.name?.charAt(0) || 'L'}
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">
                              {booking.lawyer?.user?.name || 'Lawyer'}
                            </h3>
                            <p className="text-sm text-slate-500">
                              {booking.lawyer?.specializations?.join(', ')}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {booking.date}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {booking.time_slot}
                              </span>
                              <span className="capitalize">{booking.consultation_mode}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={getStatusBadge(booking.status)}>
                            {booking.status}
                          </span>
                          {booking.status === 'accepted' && (
                            <Link to={`/chat/booking/${booking.id}`}>
                              <Button 
                                size="sm"
                                className="bg-[#075e54] hover:bg-[#054c44]"
                                data-testid={`chat-btn-${idx}`}
                              >
                                <MessageCircle className="w-4 h-4 mr-1" />
                                Chat
                              </Button>
                            </Link>
                          )}
                          {booking.status === 'pending' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => cancelBooking(booking.id)}
                              data-testid={`cancel-booking-${idx}`}
                            >
                              Cancel
                            </Button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="past" className="p-6">
              {pastBookings.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No past consultations</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pastBookings.map((booking, idx) => (
                    <motion.div
                      key={booking.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="p-4 bg-slate-50 rounded-xl border border-slate-200"
                      data-testid={`past-booking-${idx}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-slate-200 rounded-xl flex items-center justify-center font-bold text-slate-600">
                            {booking.lawyer?.user?.name?.charAt(0) || 'L'}
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">
                              {booking.lawyer?.user?.name || 'Lawyer'}
                            </h3>
                            <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                              <span>{booking.date}</span>
                              <span>{booking.time_slot}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={getStatusBadge(booking.status)}>
                            {booking.status}
                          </span>
                          {booking.status === 'completed' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setReviewModal({ open: true, booking })}
                              data-testid={`review-btn-${idx}`}
                            >
                              <Star className="w-4 h-4 mr-1" />
                              Review
                            </Button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Review Modal */}
      <Dialog open={reviewModal.open} onOpenChange={(open) => setReviewModal({ ...reviewModal, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave a Review</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Rating</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReviewData({ ...reviewData, rating: star })}
                    data-testid={`star-${star}`}
                  >
                    <Star 
                      className={`w-8 h-8 ${star <= reviewData.rating ? 'text-amber-500 fill-amber-500' : 'text-slate-300'}`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Your Review</label>
              <Textarea
                value={reviewData.comment}
                onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                placeholder="Share your experience..."
                rows={4}
                data-testid="review-comment-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewModal({ open: false, booking: null })}>
              Cancel
            </Button>
            <Button onClick={submitReview} disabled={submitting} data-testid="submit-review-btn">
              {submitting ? 'Submitting...' : 'Submit Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDashboard;
